<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { ExtractionResult, TemplateValidationError } from '@/api';
import type { OfficeTemplateValidationStatus } from '@/office/stores/officeTemplateStore';
import { selectTemplateFieldInDocument, selectTextInDocument } from '@/office/word/templateWord';
import type { FlattenedField } from '@/utils/contractGeneration/templateSchemaFields';
import { locatableIssueTarget } from '@/utils/contractGeneration/templateValidationIssues';
import TemplateFieldsCard from './TemplateFieldsCard.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps<{
  status: OfficeTemplateValidationStatus;
  result: ExtractionResult | null;
  errorMessage: string | null;
}>();

const emit = defineEmits<{
  (e: 'edit-field', field: FlattenedField): void;
}>();

// Lets the user dismiss the result banner; it comes back on the next test run
// (status flips through 'running' again).
const dismissed = ref<boolean>(false);

watch(
  () => props.status,
  status => {
    if (status === 'running') dismissed.value = false;
  }
);

const validationErrors = computed<TemplateValidationError[]>(
  () => props.result?.validation_errors ?? []
);

const hasBlockingErrors = computed<boolean>(() =>
  validationErrors.value.some(item => (item.severity ?? 'error') === 'error')
);

const issuesTitle = computed(
  () =>
    `${validationErrors.value.length} issue${validationErrors.value.length === 1 ? '' : 's'} found`
);

// ── Click-to-locate for errors ──────────────────────────────────────────
// Some messages name the offending spot (see `locatableIssueTarget`), and we
// let the user click to jump there in Word.

const hasLocatableIssue = computed(() =>
  validationErrors.value.some(item => locatableIssueTarget(item.message) !== null)
);

const message = useGaiaToast();
const locating = ref<boolean>(false);

async function locateError(messageText: string): Promise<void> {
  const target = locatableIssueTarget(messageText);
  if (!target || locating.value) return;
  locating.value = true;
  try {
    let found: boolean;
    if (target.mode === 'text') {
      found = await selectTextInDocument(target.query);
    } else {
      const result = await selectTemplateFieldInDocument(target.query);
      found = result.found;
    }
    if (!found) {
      message.warning("Couldn't find that spot in the document — it may have changed.");
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to locate the issue.');
  } finally {
    locating.value = false;
  }
}
</script>

<template>
  <n-flex v-if="status !== 'idle'" vertical :size="12" :wrap="false">
    <gaia-banner
      v-if="status === 'error' && !dismissed"
      type="error"
      title="Validation failed"
      closable
      @close="dismissed = true"
    >
      {{ errorMessage ?? 'Failed to validate the document.' }}
    </gaia-banner>

    <template v-else-if="status === 'done' && result">
      <gaia-banner
        v-if="validationErrors.length === 0 && !dismissed"
        plain
        type="success"
        closable
        @close="dismissed = true"
      >
        The document is a valid template — no issues found.
      </gaia-banner>

      <gaia-banner
        v-else-if="validationErrors.length > 0 && !dismissed"
        :type="hasBlockingErrors ? 'error' : 'warning'"
        :title="issuesTitle"
        closable
        @close="dismissed = true"
      >
        <n-flex vertical :size="6" :wrap="false">
          <!-- An issue that names its spot is a way into the document. -->
          <gaia-text
            v-for="(item, index) in validationErrors"
            :key="index"
            size="dense"
            :tone="(item.severity ?? 'error') === 'error' ? 'alert' : 'warning'"
            :class="{ locatable: locatableIssueTarget(item.message) !== null }"
            :style="{ lineHeight: 1.45 }"
            @click="locatableIssueTarget(item.message) && locateError(item.message)"
          >
            {{ item.message }}
          </gaia-text>
        </n-flex>
        <template v-if="hasLocatableIssue" #meta>
          Click an underlined issue to jump to it in the document.
        </template>
      </gaia-banner>

      <template-fields-card
        :fields="result.fields"
        title="Detected fields"
        default-expanded
        editable
        @edit="field => emit('edit-field', field)"
      />
    </template>
  </n-flex>
</template>

<style scoped>
/* A dotted underline marks the issues that can be jumped to; GaiaText has no
   prop for a text-decoration, so the one rule lives here. */
.locatable {
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}
</style>
